import { ChevronLeft, Copy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { ServicePackage } from './types';
import { PACKAGE_TYPE_LABELS, RevisionFormState } from './types';
import { ServicePackageRevisionTable } from './components/ServicePackageRevisionTable';
import i18n from '@/utils/i18n';

interface ServicePackageDetailViewProps {
  pkg: ServicePackage;
  /** Provided when rendered inside the list page; omit when embedded in a store tab */
  onBack?: () => void;
  onSave: (term: number, data: RevisionFormState) => Promise<void>;
  onCopyToTerm: (sourceRevisionId: string, targetTerm: number) => Promise<void>;
  /** Called when the user wants to copy this plan to another currency */
  onClone?: () => void;
}

export function ServicePackageDetailView({
  pkg,
  onBack,
  onSave,
  onCopyToTerm,
  onClone,
}: ServicePackageDetailViewProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-6 tw:p-6">
      {/* Header */}
      <div className="tw:flex tw:flex-col tw:gap-3">
        {onBack && (
          <Button
            variant="ghost"
            size="sm"
            className="tw:w-fit tw:-ml-2"
            onClick={onBack}
          >
            <ChevronLeft className="tw:size-4" />
            {i18n.t('ServicePackage:allPackages')}
          </Button>
        )}

        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
          <div className="tw:flex tw:items-center tw:gap-3">
            <h1 className="tw:text-2xl tw:font-semibold">
              {PACKAGE_TYPE_LABELS[pkg.package_type]}
            </h1>
            <Badge variant="outline">{pkg.currency}</Badge>
          </div>

          {onClone && (
            <Button variant="outline" size="sm" onClick={onClone}>
              <Copy className="tw:size-4" />
              {i18n.t('ServicePackage:copyToCurrency')}
            </Button>
          )}
        </div>

        <p className="tw:text-sm tw:text-muted-foreground">
          {i18n.t('ServicePackage:pricingDescription')}
        </p>
      </div>

      <ServicePackageRevisionTable
        revisions={pkg.revisions}
        currency={pkg.currency}
        onSave={onSave}
        onCopyToTerm={onCopyToTerm}
      />
    </div>
  );
}
